import { Box, Chip, Typography, useTheme } from '@mui/material'
import type { MasterAgreement, VersionedDeal } from 'utils/agreementVersioning'
import { formatDateString } from 'utils/formatDayjsDateTime'

interface VersionInfoBannerProps {
  agreement: MasterAgreement
  activeVersionCount?: number
  selectedVersion?: VersionedDeal | null
  onCompareClick?: () => void
}

const VersionInfoBanner = ({ agreement, activeVersionCount = 1, selectedVersion, onCompareClick }: VersionInfoBannerProps) => {
  const theme = useTheme()
  const { agreementFamily } = agreement
  const displayVersion = selectedVersion ?? agreement.currentVersion
  const hasMultiple = activeVersionCount > 1

  return (
    <Box
      onClick={hasMultiple && onCompareClick ? onCompareClick : undefined}
      sx={{
        px: 2,
        py: 1,
        borderRadius: 1,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(107, 185, 240, 0.08)' : 'rgba(107, 185, 240, 0.06)',
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(107, 185, 240, 0.2)' : 'rgba(107, 185, 240, 0.15)',
        ...(hasMultiple && onCompareClick
          ? { cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          Active {agreementFamily} Agreement Detected
        </Typography>
        <Chip
          label={`v${displayVersion.semanticVersion}`}
          size="small"
          color="primary"
          sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, fontSize: 11 }}
        />
        {hasMultiple && (
          <Chip
            label={`${activeVersionCount} active — click to compare`}
            size="small"
            variant="outlined"
            color="warning"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
        {hasMultiple ? 'Versioning from: ' : 'Current: '}
        {displayVersion.deal.name}
        {displayVersion.deal.startDate && ` | ${formatDateString(displayVersion.deal.startDate)}`}
        {displayVersion.deal.endDate && ` - ${formatDateString(displayVersion.deal.endDate)}`}
        {` | ${displayVersion.products.length} product(s)`}
      </Typography>
    </Box>
  )
}

export default VersionInfoBanner
